import { useEffect, useState } from 'react';
import { api, generatePdf } from '../lib/api';
import type { ConnectorListItem, TemplateRow } from '../lib/types';

export default function TestGenerate() {
  const [templates, setTemplates] = useState<TemplateRow[]>([]);
  const [connectors, setConnectors] = useState<ConnectorListItem[]>([]);
  const [apiKey, setApiKey] = useState('');
  const [templateId, setTemplateId] = useState('');
  const [data, setData] = useState('{\n  "field1": "Hello world"\n}');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<{ kind: 'success' | 'error'; text: string; generationId?: string; size?: number } | null>(null);

  useEffect(() => {
    api.listTemplates().then((t) => {
      setTemplates(t);
      if (t[0]) setTemplateId(t[0].id);
    });
    api.listConnectors().then(setConnectors);
  }, []);

  async function run() {
    setBusy(true);
    setResult(null);
    try {
      const parsed = JSON.parse(data);
      const { blob, generationId } = await generatePdf({ template_id: templateId, data: parsed }, apiKey || undefined);
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank');
      setResult({ kind: 'success', text: `Generated ${blob.size} bytes`, generationId: generationId || undefined, size: blob.size });
    } catch (e) {
      setResult({ kind: 'error', text: (e as Error).message });
    } finally {
      setBusy(false);
    }
  }

  return (
    <div>
      <div className="card">
        <h3>Test generate</h3>
        <p className="muted">Use this to verify a template + connector works end-to-end. The PDF opens in a new tab.</p>

        <div className="field">
          <label>Template</label>
          <select value={templateId} onChange={(e) => setTemplateId(e.target.value)}>
            <option value="">— pick a template —</option>
            {templates.map((t) => (
              <option key={t.id} value={t.id}>{t.name}</option>
            ))}
          </select>
        </div>

        <div className="field">
          <label>API key (optional, from a connector)</label>
          <input
            type="password"
            value={apiKey}
            onChange={(e) => setApiKey(e.target.value)}
            placeholder="pk_live_... — leave empty for the dev bypass"
          />
          <div className="muted" style={{ fontSize: 11, marginTop: 4 }}>
            If empty and DEV_BYPASS_AUTH is on, no auth is required. If you have a connector, paste its key here to test the real path.
          </div>
        </div>

        <div className="field">
          <label>Data (JSON)</label>
          <textarea
            value={data}
            onChange={(e) => setData(e.target.value)}
            rows={10}
            spellCheck={false}
          />
        </div>

        <div className="row">
          <button onClick={run} disabled={!templateId || busy}>{busy ? 'Generating…' : 'Generate & open'}</button>
          {result && (
            <span className={result.kind === 'success' ? 'pill success' : 'pill danger'}>
              {result.text} {result.generationId && `· ${result.generationId.slice(0, 8)}`}
            </span>
          )}
        </div>
      </div>

      {connectors.length > 0 && (
        <div className="card">
          <h3>Existing connectors</h3>
          <table className="table">
            <thead>
              <tr><th>Name</th><th>Type</th><th>Active</th></tr>
            </thead>
            <tbody>
              {connectors.map((c) => (
                <tr key={c.id}>
                  <td className="mono">{c.name}</td>
                  <td><span className="pill info">{c.type}</span></td>
                  <td>{c.is_active ? <span className="pill success">active</span> : <span className="pill">disabled</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
